import { useCallback, useMemo, useState } from 'react';
import { withNamespaces } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import moment from 'moment';
import BootstrapTable from 'react-bootstrap-table-next';
import { INTERNATIONAL_DATE_FORMAT } from 'src/common/constants';
import DateUtils from 'src/services/utils/DateUtils';
import {
    Button,
    Card,
    CardBody,
    Col,
    Row,
    UncontrolledTooltip,
} from 'reactstrap';

import {
    ROUTE_PATH_ADMIN_GUARD_BID_SUMMARY,
    ROUTE_PATH_ADMIN_GUARD_BID_VERIFICATION_FLOW,
} from 'src/modules/3rd-party-management/constants';
import FilteringComponent from './FilteringComponent';
import { BidStatuses } from '../constants';
import SortingComponent from './SortingComponent';
import VerificationProgress from './VerificationProgress';
import { RiNodeTree } from 'react-icons/ri';

const CarrierBids = ({
    bids = [],
    freightId,
    totalBids,
    t,
}) => {
    const history = useHistory();
    const dateUtils = useMemo(() => new DateUtils(), []);
    const [filters, setFilters] = useState({
        search: undefined,
        status: undefined,
        sortBy: 'amount_asc',
    });

    const handleFilterChange = useCallback((newFilters) => {
        setFilters((currentFilters) => ({
            ...currentFilters,
            ...newFilters,
        }));
    }, []);

    const handleSortChange = useCallback((sortBy) => {
        setFilters((currentFilters) => ({
            ...currentFilters,
            sortBy,
        }));
    }, []);

    const openBidSummary = useCallback((bidId) => {
        history.push(
            ROUTE_PATH_ADMIN_GUARD_BID_SUMMARY
                .replace(':freightId', freightId)
                .replace(':bidId', bidId)
        );
    }, [
        freightId,
        history,
    ]);

    const openBidVerificationFlow = useCallback((bidId) => {
        history.push(
            ROUTE_PATH_ADMIN_GUARD_BID_VERIFICATION_FLOW
                .replace(':freightId', freightId)
                .replace(':bidId', bidId)
        );
    }, [
        freightId,
        history,
    ]);

    const filteredBids = useMemo(() => {
        const normalizedSearch = (filters.search || '').trim().toLowerCase();
        const result = bids.filter((bid) => (
            (!normalizedSearch || bid.carrier.toLowerCase().includes(normalizedSearch))
            && (!filters.status || bid.status === filters.status)
        ));

        return [...result].sort((first, second) => (
            filters.sortBy === 'amount_desc'
                ? second.amount - first.amount
                : first.amount - second.amount
        ));
    }, [
        bids,
        filters.search,
        filters.sortBy,
        filters.status,
    ]);

    const columns = useMemo(() => [
        {
            dataField: 'rowNumber',
            text: '#',
            formatter: (_cell, _row, rowIndex) => rowIndex + 1,
            style: { width: '60px' },
        },
        {
            dataField: 'carrier',
            text: t('Carrier'),
            formatter: (carrier, row) => (
                <div className="d-flex align-items-center gap-2">
                    <img alt={carrier} className="avatar-xs rounded-circle" src={row.logo} />
                    <div>
                        <b className="d-block">{carrier}</b>
                        <small className="text-muted">
                            {`${row.carrierCountry} · ${row.headquarters}`}
                        </small>
                    </div>
                </div>
            ),
        },
        {
            dataField: 'amount',
            text: t('Bid Amount'),
            formatter: (amount, row) => (
                <div>
                    <b className="d-block">
                        {new Intl.NumberFormat('de-DE', {
                            style: 'currency',
                            currency: 'EUR',
                            maximumFractionDigits: 0,
                        }).format(amount)}
                    </b>
                    <small className="text-muted">
                        {`${row.deliveryDays} ${t('days delivery')}`}
                    </small>
                </div>
            ),
        },
        {
            dataField: 'verificationProgress',
            text: t('Verification Progress'),
            formatter: (verificationProgress) => (
                <VerificationProgress stages={verificationProgress} />
            ),
        },
        {
            dataField: 'status',
            text: t('Status'),
            formatter: (bidStatus) => {
                const statusInfo = BidStatuses[bidStatus];

                return (
                    <span className={`badge p-2 font-size-13 ${statusInfo.className}`}>
                        <i className={`${statusInfo.icon} align-middle me-1 font-size-16`} />
                        {t(bidStatus)}
                    </span>
                );
            },
        },
        {
            dataField: 'createdAt',
            text: t('Creation Date'),
            formatter: (createdAt) => (
                createdAt ? (
                    <div>
                        <span className="d-block">
                            {dateUtils.convertDateToDate(createdAt, INTERNATIONAL_DATE_FORMAT)}
                        </span>
                        <small className="text-muted">
                            {moment(createdAt).fromNow()}
                        </small>
                    </div>
                ) : (
                    <span className="text-muted">-</span>
                )
            ),
        },
        {
            dataField: 'assignedAnalyst',
            text: t('Verification Analyst'),
            formatter: (assignedAnalyst) => {
                if (!assignedAnalyst?.name) {
                    return <span className="text-muted">-</span>;
                }

                return (
                    <div className="d-flex align-items-center gap-2">
                        <span
                            className="avatar-xs rounded-circle bg-primary-subtle text-primary d-inline-flex align-items-center justify-content-center font-size-12 fw-semibold"
                        >
                            {assignedAnalyst.initials}
                        </span>
                        <span>{assignedAnalyst.name}</span>
                    </div>
                );
            },
        },
        {
            dataField: 'actions',
            text: t('Action'),
            formatter: (_cell, row) => (
                <div className="d-flex align-items-center">
                    <Button
                        color="primary"
                        onClick={() => openBidSummary(row.id)}
                        outline
                        size="sm"
                        type="button"
                    >
                        {t('View')}
                    </Button>
                    <Button
                        aria-label={t('Live Verification Flow')}
                        id={`bid-verification-flow-${row.id}`}
                        className="ms-1"
                        color="primary"
                        onClick={() => openBidVerificationFlow(row.id)}
                        outline
                        size="sm"
                        type="button"
                    >
                        <RiNodeTree size={18} />
                    </Button>
                    <UncontrolledTooltip target={`bid-verification-flow-${row.id}`}>
                        {t('Live Verification Flow')}
                    </UncontrolledTooltip>
                    <Button aria-label={t('Bid actions')} className="border-0" color="primary" outline>
                        <i className="ri-more-2-fill" />
                    </Button>
                </div>
            ),
            style: { width: '180px' },
        },
    ], [
        dateUtils,
        openBidVerificationFlow,
        openBidSummary,
        t,
    ]);

    return (
        <Card>
            <CardBody>
                <Row className="align-items-center mb-3">
                    <Col lg="4">
                        <h5 className="mb-3 mb-lg-0">
                            {`${t('Carrier Bids')} (${totalBids})`}
                        </h5>
                    </Col>
                    <Col lg="8">
                        <div className="d-flex flex-wrap justify-content-lg-end align-items-center gap-2">
                            <FilteringComponent
                                onChange={handleFilterChange}
                            />

                            <SortingComponent
                                onChange={handleSortChange}
                            />
                        </div>
                    </Col>
                </Row>

                <BootstrapTable
                    bordered={false}
                    classes="table tpdd-table"
                    columns={columns}
                    data={filteredBids}
                    headerWrapperClasses="thead-light"
                    keyField="id"
                    responsive
                    striped
                    wrapperClasses="table-responsive"
                />
            </CardBody>
        </Card>
    );
};

export default withNamespaces()(
    CarrierBids
);
